/**
 * 柱状分布图数据处理模块
 */

/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as BarDistributionLayerSpec } from '../../../../layer/preset/bar/barDistribution';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface BarDistributionLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type BarDistributionLayerType = StandardChartLayerSpec &
  BarDistributionLayerSpec &
  BarDistributionLayerTypeWithFields;

// 定义子项数据接口
interface ChildItem {
  name: string;
  value: number;
}

// 定义数据项接口
interface DataItem {
  [key: string]: string | number | Date | ChildItem[] | null | undefined;
  children?: ChildItem[];
}

// 创建柱状分布图数据类型验证器
const validateBarDistributionDataTypes = createDataTypeValidator('barDistribution', [
  { field: 'x', type: ['string', 'date'] },
  { field: 'y', type: 'number' }
]);

// 创建柱状分布图编码验证器
const validateBarDistributionEncoding = createEncodingValidator([{ field: 'x' }, { field: 'y' }]);

// 额外的检查函数
const additionalBarDistributionChecks = (layer: BarDistributionLayerType, data: Data) => {
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return;
  }

  // 检查每条数据是否包含children数组
  const hasValidChildren = (values as DataItem[]).every(item => {
    if (!Array.isArray(item.children)) {
      return false;
    }

    // 检查children中的每个元素是否包含name和value属性
    return item.children.every(
      child => typeof child.name === 'string' && typeof +child.value === 'number'
    );
  });

  if (!hasValidChildren) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires each data item to have a "children" array with "name" (string) and "value" (number) properties`
    );
    layer.__isEncodingValid = false;
  }

  // 检查数据量 - 建议数据量不超过10条
  if (values.length > 10) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" chart works best with 10 or fewer data points for better readability`
    );
  }

  // 检查数据量 - 至少需要2条数据才能体现分布特性
  if (values.length < 2) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" chart requires at least 2 data points to show distribution`
    );
  }
};

// 创建并导出柱状分布图处理器
export const barDistribution = createLayerProcessor<BarDistributionLayerType>(
  'barDistribution',
  validateBarDistributionEncoding,
  validateBarDistributionDataTypes,
  additionalBarDistributionChecks
);
